// Parser contains JSON parser for transforming input JSON into our definition of API descriptors

import { ModelType, type UnwrappedApiServiceModelDescriptor } from "@doitintl/cmp-models";
import { DateTime } from "luxon";

class ModelDescriptorParsingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ModelDescriptorParsingError";
  }
}

function isFloat(v: number) {
  return Number.isFinite(v) && !Number.isInteger(v);
}

function isISODateString(v: string) {
  const date = DateTime.fromISO(v);
  return date.isValid;
}

function isUnixSecondsTimestamp(v: number) {
  const date = DateTime.fromSeconds(v);
  return date.isValid && v > 10000000 && date.year >= 1970 && date.year <= 2100;
}

function isUnixMillisTimestamp(v: number) {
  const date = DateTime.fromMillis(v);
  return date.isValid && v > 100000000000 && date.year >= 1970 && date.year <= 2100;
}

function getObjectFormat(obj: unknown): UnwrappedApiServiceModelDescriptor {
  if (obj === null) {
    throw new ModelDescriptorParsingError("input cannot be null");
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      throw new ModelDescriptorParsingError("input cannot be an empty list");
    }

    const isEqual = obj
      .slice(0, 5)
      .map((v) => JSON.stringify(getObjectFormat(v)))
      .every((v, _, arr) => v === arr[0]);

    if (!isEqual) throw new ModelDescriptorParsingError("list elements must be the same type");

    return {
      type: ModelType.LIST,
      member: { model: getObjectFormat(obj[0]) },
    };
  }

  switch (typeof obj) {
    case "string":
      if (isISODateString(obj)) {
        return {
          type: ModelType.TIMESTAMP,
          timestampFormat: "yyyy-MM-dd'T'HH:mm:ss.SSSZZZ",
        };
      }

      return {
        type: ModelType.STRING,
      };

    case "number":
      if (isFloat(obj)) {
        return {
          type: ModelType.FLOAT,
        };
      }

      if (isUnixSecondsTimestamp(obj)) {
        return {
          type: ModelType.TIMESTAMP,
          timestampFormat: "X",
        };
      }

      if (isUnixMillisTimestamp(obj)) {
        return {
          type: ModelType.TIMESTAMP,
          timestampFormat: "x",
        };
      }

      return {
        type: ModelType.INTEGER,
      };

    case "boolean":
      return { type: ModelType.BOOLEAN };

    case "object": {
      const structure: UnwrappedApiServiceModelDescriptor = { type: ModelType.STRUCTURE, members: {} };

      for (const key in obj) {
        structure.members[key] = { model: getObjectFormat((obj as Record<string, unknown>)[key]) };
      }

      return structure;
    }

    default:
      throw new ModelDescriptorParsingError(`Unable to handle ${typeof obj}`);
  }
}

/**
 * Converts an object to a JSON string that describes its format
 *
 * @param obj - The object to analyze
 */
export function getModelDescriptorFromObject(obj: unknown): UnwrappedApiServiceModelDescriptor {
  return getObjectFormat(obj);
}
